<template>
    <ion-app :class="{ 'web-view': $platform === 'web' }">
        <div class="web-container" v-if="$platform === 'web'">
            <ion-router-outlet id="main-content" />
            <SideMenu></SideMenu>
        </div>
        <template v-else>
            <ion-router-outlet id="main-content" />
            <SideMenu></SideMenu>
        </template>
        <!-- Image sync notification -->
        <!-- <SyncNotification
            v-if="isLoggedIn"
            @sync-notification-shown="handleSyncNotificationShown"
            @sync-notification-hidden="handleSyncNotificationHidden"
        /> -->
    </ion-app>
</template>

<script lang="js">

import { IonApp, IonRouterOutlet, toastController, modalController } from '@ionic/vue';
import { App } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { informationCircle } from 'ionicons/icons';
import { Browser } from '@capacitor/browser';
import Assistant from './components/Assistant.vue';
import ohmi from './assets/ohmi_wit.svg'
import { alertController } from '@ionic/vue';
import SideMenu from './views/layout/SideMenu.vue';
// import SyncNotification from './components/SyncNotification.vue';
import UpdateModal from './components/UpdateModal.vue';
import updateService from './services/updateService';
import { liveQuery } from "dexie";
import { db } from './services/db';
import { Camera } from '@capacitor/camera';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import runSpeedTest from './services/speedtest';
import { syncApi } from './services/index';
import eventBus from './services/eventBus';

// Import test utilities in development mode
if (process.env.NODE_ENV === 'development') {
    import('./utils/testMultiImageSync').then(() => {
        console.log('Multi-image sync test utilities loaded');
    });
    import('./utils/testSyncNotification').then(() => {
        console.log('Sync notification test scenarios loaded');
    });
    import('./utils/clearEntityNotFoundJobs').then(() => {
        console.log('Entity not found job cleaner loaded');
    });
    import('./utils/cleanupJobs').then(() => {
        console.log('Job cleanup utility loaded');
    });
}

export default {
    name: 'App',
    components: {
        IonApp,
        IonRouterOutlet,
        SideMenu,
        // SyncNotification,
    },
    data() {
        return {
            ohmi,
            informationCircle,
            toggle: null,
            dark_mode: false,
            is_active: true,
            jobs: [],
            subscription: null,
            debounceTimer: null,
            appState: null,
            appStateListener: null,
            alert_show: false,
            debounce_echo: null,
            wasProcessingQueue: false,
            wasSyncing: false,
            syncNotificationActive: false,
            backButtonListener: null,
        }
    },

    // Add method to explicitly update app state in store
    created() {
        // Update store with initial app state
        this.$store.dispatch('setAppActive', true);
    },
    async mounted() {
        this.dark_mode = this.$store.getters.getDarkMode;
        document.body.classList.toggle('dark', this.dark_mode);
        // Update check now happens in store on app state change
        this.setJobs()
        this.getState()
        this.setupKeyboardShortcuts()
        this.setupNotificationListeners()
        if (this.$platform !== 'web') {
            this.setInitialOrientation();
            this.listenToOrientationChanges();

            // Ensure network state is correctly initialized
            // This is especially important for iOS
            try {
                const networkStatus = await Network.getStatus();
                this.$store.dispatch('setOnline', networkStatus.connected);
                this.$store.dispatch('setConnectionType', networkStatus.connectionType);

                console.log(`Initial network status: ${JSON.stringify(networkStatus)}`);

                // If we have jobs and are online at startup, process them immediately
                if (this.jobs > 0 && networkStatus.connected) {
                    console.log('Processing pending jobs at startup');
                    // Give the app a moment to fully initialize before processing
                    setTimeout(() => {
                        syncApi.processQueueWithParams({ ignoreOffline: true });
                    }, 1000);
                }
            } catch (err) {
                console.error('Error checking initial network status:', err);
            }
        }
    },
    beforeUnmount() {
        this.removeOrientationListener();
        this.removeKeyboardShortcuts();
        this.removeBackButtonHandler();
        
        if (this.boundHandleBackgroundNotification) {
            eventBus.off('show-background-notification', this.boundHandleBackgroundNotification);
        }
        eventBus.off('app-going-background');

        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    },
    methods: {
        async setInitialOrientation() {
            const orientation = await ScreenOrientation.orientation();

            const type = orientation.type.startsWith('portrait') ? 'portrait' : 'landscape';
            this.$store.dispatch('setOrientation', type);
        },
        async listenToOrientationChanges() {
            this.orientationListener = await ScreenOrientation.addListener('screenOrientationChange', (event) => {
                const type = event.type.startsWith('portrait') ? 'portrait' : 'landscape';
                this.$store.dispatch('setOrientation', type);
            });
        },
        async removeOrientationListener() {
            if (this.orientationListener) {
                await this.orientationListener.remove();
                this.orientationListener = null;
            }
        },
        async checkPermissions() {
            // Only request camera permission (not photos/gallery)
            // to comply with Google Play's photo picker requirements
            const camera_permissions = await Camera.checkPermissions();

            if (camera_permissions.camera === 'denied') {
                Camera.requestPermissions({ permissions: ['camera'] });
            }

            // Don't request filesystem permissions on startup
            // Android 13+ uses scoped storage and photo picker which don't require broad permissions
            // Permissions will be requested only when needed (e.g., local backup feature)
        },
        async getState() {
            const appState = await App.getState();
            this.appState = appState;

            if (this.appStateListener === null) {
                this.appStateListener = App.addListener('appStateChange', (state) => {
                    this.appState = state;

                    // Update active state in store - this is used by components like Camera.vue
                    this.$store.dispatch('setAppActive', state.isActive);

                    console.log('App state changed:', state);

                    // When app becomes inactive (goes to background)
                    if (!state.isActive) {
                        // Save current worker states
                        this.wasProcessingQueue = this.$store.getters.queueRunning;
                        this.wasSyncing = this.$store.getters.syncRunning;
                        
                        // Emit event for sync notification component
                        eventBus.emit('app-going-background');

                        // Check for unsynchronized jobs, with special handling for images
                        this.checkPendingImageJobs();

                        // Pause any active workers/sync operations
                        if (this.wasProcessingQueue || this.wasSyncing) {
                            console.log('App going to background, pausing workers');
                            syncApi.stopAll();
                        }
                    }
                    // When app becomes active again
                    else if (state.isActive && this.$platform != 'web') {
                        console.log('App returning to foreground');

                        // Clear any offline force state
                        this.$store.dispatch('setForceOffline', false);

                        // if we are logged in, process queue
                        if (this.isLoggedIn) {
                            console.log('App is active and user is logged in - processing queue');
                            // Check if we have jobs to sync
                            if (this.jobs > 0) {
                                syncApi.processQueue()
                            }
                        }
                    }
                });
            }
        },
        // Method that components can call to start monitoring app state
        monitorAppState() {
            if (!this.appStateListener) {
                this.getState();
            }
        },

        async checkServerStatus() {
            if (!localStorage.getItem('api_url')) {
                return;
            }

            this.$axios
                .get(localStorage.getItem('api_url') + "/status", { timeout: 5000 }) // Timeout ingesteld op 5 seconden
                .then((response) => {
                    if (response.status >= 200 && response.status < 300) {
                        this.$store.dispatch("setServerStatus", "online");
                    } else {
                        console.error(`Unexpected status code: ${response.status}. Sync will stop.`);
                        this.$requests.queue_running = false;
                        this.$store.dispatch("setSyncProcess", "idle");
                        this.$store.dispatch("setServerStatus", "offline");
                    }
                })
                .catch((error) => {
                    if (error.code === "ECONNABORTED") {
                        // Timeout error
                        console.error("Connection timed out. The server did not respond in time.");
                    } else if (!error.response) {
                        // Andere netwerkfout, zoals geen internet
                        console.error("No internet connection or server is unreachable.");
                    } else {
                        // Server reageerde met een foutstatuscode
                        console.error(`HTTP error: ${error.response.status}`);
                    }
                    this.$requests.queue_running = false;
                    this.$store.dispatch("setSyncProcess", "idle");
                    this.$store.dispatch("setServerStatus", "offline");
                });
        },
        async showUpdateModal() {
            // Prevent opening multiple modals using shared store flag
            if (this.$store.getters.getUpdateModalOpen) {
                console.log('Update modal already open, skipping...');
                return;
            }

            this.$store.dispatch('setUpdateModalOpen', true);

            const modal = await modalController.create({
                component: UpdateModal,
                cssClass: 'update-modal',
                backdropDismiss: true,
                mode: 'ios'
            });

            // Reset flag when modal is dismissed
            modal.onDidDismiss().then(() => {
                this.$store.dispatch('setUpdateModalOpen', false);
            });

            await modal.present();
        },
        open(url, target) {
            Browser.open({ url: url, windowName: target });
        },
        async getGlobalSettings() {
            const client_code = localStorage.getItem('client_code')

            if (!client_code || !this.$store.getters.getUser?.email) {
                return;
            }

            console.log('Getting global settings for client: ' + client_code)

            this.$axios.get('https://brokert.novacheck.nl/api/client-url?email=' + this.$store.getters.getUser.email)
                .then(response => {
                    // we loop over the clients we have and check if the client_code is in the list
                    response.data.forEach((record) => {
                        if (record.client?.code === client_code) {
                            this.$store.dispatch('setGlobalSettings', record.client.settings)
                        }
                    })
                }).catch((error) => {
                    console.log(error)
                    this.loading = false;
                })
        },
        async info(message, url) {
            const toast = await toastController.create({
                message: message,
                duration: 10000,
                color: "primary",
                position: "bottom",
                icon: informationCircle,
                buttons: [
                    {
                        text: "Openen",
                        role: "open",
                        handler() {
                            Browser.open({ url: url })
                        }
                    },
                    {
                        text: "Sluiten",
                        role: "cancel",
                    },
                ],
            });
            return toast.present();
        },
        async showNotification(title, message) {
            await LocalNotifications.requestPermissions();
            await LocalNotifications.registerActionTypes({
                types: [
                    {
                        id: 'choices',
                        actions: [
                            {
                                id: 'dismiss',
                                title: 'Negeren',
                                destructive: true
                            },
                            {
                                id: 'open',
                                title: 'Open Novacheck',
                                foreground: true,
                                destructive: false,
                            },
                        ]
                    }
                ]
            });

            LocalNotifications.schedule({
                notifications: [
                    {
                        id: 1,
                        title: title,
                        body: message,
                        actionTypeId: 'choices'
                    }
                ],
                allowWhileIdle: true
            });
        },
        async setJobs() {
            const observable = liveQuery(
                async () => await db.jobs.count()
            );

            if (this.subscription) {
                this.subscription.unsubscribe();
            }

            this.subscription = observable.subscribe({
                next: result => {
                    this.jobs = result
                    console.log('Jobs:', result)
                },
                error: error => console.error(error)
            });
        },
        debounce(func, wait) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(func, wait);
        },
        openOhmi() {
            const modal = modalController.create({
                component: Assistant,
                initialBreakpoint: 1,
                breakpoints: [0, 1]
            });

            modal.then((modal) => {
                modal.present();
            });

        },
        setStatusBar() {
            if (this.$platform !== 'web') {
                const route_name = this.$route.name;
                if (route_name == 'login') {
                    StatusBar.setBackgroundColor({ color: "#024140" });
                    StatusBar.setStyle({ style: Style.Dark });
                } else {
                    if (this.$store.getters.getDarkMode) {
                        StatusBar.setBackgroundColor({ color: "#333333" });
                        StatusBar.setStyle({ style: Style.Dark });
                    } else {
                        StatusBar.setBackgroundColor({ color: "#ffffff" });
                        StatusBar.setStyle({ style: Style.Light });
                    }
                }
            }
        },
        setupKeyboardShortcuts() {
            this.handleKeyboardShortcut = (event) => {
                // Check for Ctrl+Shift+D (or Cmd+Shift+D on Mac)
                if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'd') {
                    event.preventDefault();
                    this.toggleDarkMode();
                }
            };
            
            document.addEventListener('keydown', this.handleKeyboardShortcut);
        },
        removeKeyboardShortcuts() {
            if (this.handleKeyboardShortcut) {
                document.removeEventListener('keydown', this.handleKeyboardShortcut);
            }
        },
        toggleDarkMode() {
            const currentDarkMode = this.$store.getters.getDarkMode;
            this.$store.dispatch('setDarkMode', !currentDarkMode);
        },
        
        async checkPendingImageJobs() {
            if (this.$platform === 'web') return;
            
            try {
                // Count image-specific jobs
                const imageJobs = await db.jobs
                    .where('type')
                    .anyOf(['image-s3-upload', 'image-confirm'])
                    .count();
                
                // Count other jobs
                const otherJobs = this.jobs - imageJobs;
                
                if (imageJobs > 0) {
                    // Show specific notification for image sync
                    const title = 'Afbeeldingen synchroniseren';
                    const message = `${imageJobs} afbeelding${imageJobs > 1 ? 'en' : ''} worden gesynchroniseerd. Houd de app open voor beste resultaten.`;
                    this.showNotification(title, message);
                } else if (otherJobs > 0) {
                    // Show regular notification for other jobs
                    const title = 'Niet-gesynchroniseerde taken';
                    const message = `Je hebt nog ${otherJobs} niet-gesynchroniseerde taken. Vergeet niet te synchroniseren!`;
                    this.showNotification(title, message);
                }
            } catch (error) {
                console.error('Error checking pending image jobs:', error);
            }
        },
        
        handleSyncNotificationShown() {
            this.syncNotificationActive = true;
            this.setupBackButtonHandler();
        },
        
        handleSyncNotificationHidden() {
            this.syncNotificationActive = false;
            this.removeBackButtonHandler();
        },
        
        async setupBackButtonHandler() {
            if (this.$platform === 'web' || this.backButtonListener) return;
            
            // Add back button listener to prevent accidental exit during sync
            this.backButtonListener = App.addListener('backButton', async ({ canGoBack }) => {
                if (this.syncNotificationActive) {
                    // Show confirmation before exiting
                    const alert = await alertController.create({
                        header: 'Synchroniseren actief',
                        message: 'Er worden nog afbeeldingen gesynchroniseerd. Weet je zeker dat je de app wilt afsluiten?',
                        buttons: [
                            {
                                text: 'Annuleren',
                                role: 'cancel'
                            },
                            {
                                text: 'Afsluiten',
                                role: 'destructive',
                                handler: () => {
                                    if (!canGoBack) {
                                        App.exitApp();
                                    }
                                }
                            }
                        ]
                    });
                    await alert.present();
                } else if (!canGoBack) {
                    // Normal back button behavior
                    App.exitApp();
                }
            });
        },
        
        removeBackButtonHandler() {
            if (this.backButtonListener) {
                this.backButtonListener.remove();
                this.backButtonListener = null;
            }
        },
        
        // Listen for local notification from sync component
        setupNotificationListeners() {
            this.boundHandleBackgroundNotification = (data) => {
                if (this.$platform !== 'web') {
                    this.showNotification(data.title, data.message);
                }
            };
            eventBus.on('show-background-notification', this.boundHandleBackgroundNotification);
        },
    },
    computed: {
        isLoggedIn() {
            return this.$store.getters.isAuthenticated;
        },
    },
    watch: {
        // Watch for update info being set by the store
        "$store.getters.getUpdateInfo": {
            handler(val) {
                console.log('🔔 WATCHER FIRED - getUpdateInfo changed:', JSON.stringify(val));
                console.log('🔔 Current modal open state:', this.$store.getters.getUpdateModalOpen);

                if (val && val.updateAvailable) {
                    console.log('🎉 Update detected in store:', val);

                    // Check if we should show the modal (not dismissed today)
                    const wasDismissed = updateService.wasDismissedToday();
                    console.log('Was dismissed today?', wasDismissed);

                    if (!wasDismissed) {
                        console.log('✅ Showing update modal NOW');
                        this.showUpdateModal();
                    } else {
                        console.log('⏭️ Update modal dismissed today, skipping');
                    }
                } else {
                    console.log('ℹ️ No update available or updateAvailable is false:', val);
                }
            },
            immediate: true
        },
        "$store.getters.online": {
            handler(val) {
                if (val) {
                    console.log('Network is now online - syncing jobs');

                    // Check if we have jobs to process
                    if (this.jobs > 0) {
                        console.log(`Network status change detected - force starting sync with ${this.jobs} jobs`);

                        // Use the more aggressive approach for network state changes
                        syncApi.processQueueWithParams({
                            ignoreOffline: true,
                            forceSync: true
                        }).catch(err => {
                            console.log('Error in online state queue processing:', err);

                            // If first attempt fails, try all fallbacks
                            setTimeout(() => {
                                console.log('Trying all fallbacks after online state change');
                                // Force worker reset first
                                syncApi.stopAll();

                                // Wait a moment then retry
                                setTimeout(() => {
                                    syncApi.processQueueWithParams({
                                        ignoreOffline: true,
                                        iosControlCenter: true,
                                        forceSync: true,
                                        emergencyMode: true
                                    }).catch(() => {
                                        // Last resort - just try basic queue
                                        syncApi.processQueue();
                                    });
                                }, 500);
                            }, 1000);
                        });
                    } else {
                        console.log('Network is online but no jobs to process');
                    }
                }
            },
            immediate: false,
        },
        appState: {
            handler(val) {
                if (val?.isActive) {
                    // App is active - this is the key trigger
                    console.log('App is now active - checking network and syncing if connected');

                    // Simple housekeeping
                    this.getGlobalSettings();
                    this.checkPermissions();
                    this.setStatusBar();

                    // Direct network check
                    Network.getStatus().then(networkStatus => {
                        console.log(`Network status: ${JSON.stringify(networkStatus)}`);

                        // Update store with actual status
                        this.$store.dispatch('setOnline', networkStatus.connected);
                        this.$store.dispatch('setConnectionType', networkStatus.connectionType);

                        // If connected, process queue immediately and unconditionally 
                        // Force the worker to start regardless of job count (there could be pending jobs that weren't 
                        // visible when the app went to background)
                        if (networkStatus.connected) {
                            console.log('App is active AND network is connected - processing queue NOW with aggressive force params');

                            // IMPORTANT: Use the exact same aggressive approach as the online watcher
                            if (this.jobs > 0) {
                                console.log('Processing jobs immediately after app activation');

                                syncApi.processQueueWithParams({
                                    ignoreOffline: true,
                                    forceSync: true
                                }).catch(err => {
                                    console.log('Error in app activation queue processing:', err);

                                    // If first attempt fails, try all fallbacks - exactly like online watcher
                                    setTimeout(() => {
                                        console.log('Trying all fallbacks after app activation');
                                        // Force worker reset first
                                        syncApi.stopAll();

                                        // Wait a moment then retry with emergency mode
                                        setTimeout(() => {
                                            console.log('Emergency retry for app activation');
                                            syncApi.processQueueWithParams({
                                                ignoreOffline: true,
                                                iosControlCenter: true,
                                                forceSync: true,
                                                emergencyMode: true
                                            }).catch(() => {
                                                // Last resort - just try basic queue
                                                syncApi.processQueue();
                                            });
                                        }, 500);
                                    }, 1000);
                                });
                            } else {
                                console.log('No jobs to process immediately after app activation');
                            }

                            // Also directly set appActive true in store to ensure all components are aware
                            this.$store.dispatch('setAppActive', true);
                        } else {
                            console.log('App is active but network is NOT connected - skipping sync');
                        }
                    }).catch(err => {
                        console.error('Network check error:', err);
                    });
                } else {
                    console.log('App became inactive');
                }
            },
            immediate: true,
        },
        // we watch jobs
        jobs: {
            handler(val) {
                if (val > 0) {
                    this.debounce(() => {
                        // Silently process queue - ignore any failures
                        syncApi.processQueue().catch(() => { });
                    }, 5000)
                }
            },
            immediate: true,
        },
        dark_mode: function (val) {
            this.$store.dispatch('setDarkMode', val);
            document.body.classList.toggle('dark', val);

        },
        "$route.name": {
            handler() {
                this.setStatusBar();
            },
            immediate: true,
        },
        "$store.getters.getDarkMode": {
            handler(val) {
                this.dark_mode = val;
                this.setStatusBar();
            },
            immediate: true,
        },
        // if selectionMode or dragMode is activated, vibrate lightly
        "$store.getters.getSelectionMode": {
            handler(val) {
                if (val) {
                    Haptics.impact({ style: ImpactStyle.Light });
                }
            },
            immediate: true,
        },
        "$store.getters.getDragMode": {
            handler(val) {
                if (val) {
                    Haptics.impact({ style: ImpactStyle.Light });
                }
            },
            immediate: true,
        },
    }
};
</script>

<style>
.sidebar-button-container {
    position: fixed;
    top: 88%;
    left: -5px;
    transform: translateY(-50%);
    z-index: 1000;
}

.ai-button {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--ion-color-secondary-tint);
    color: var(--ion-color-primary-contrast);
    width: 41px;
    height: 40px;
    border-radius: 0 50% 50% 0;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
    cursor: pointer;
    transition: all 0.3s;
}

.ai-button:hover {
    background-color: var(--ion-color-primary-shade);
}

.ai-button span {
    font-weight: bold;
}

.web-container {
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    height: 100%;
    background-color: #ffffff;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    position: relative;
}
</style>
